import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Pagination,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import apiClient from '../../services/api/apiClient';

interface News {
  _id: string;
  title: string;
  description: string;
  content: string;
  contentImages?: string[];
  category: 'alert' | 'tip' | 'news';
  thumbnailUrl?: string;
  isUrgent: boolean;
  link?: string;
  isPublished: boolean;
  publishedAt?: string;
  viewCount: number;
  author: string;
  createdAt: string;
  updatedAt: string;
}

const NewsPage: React.FC = () => {
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingNews, setEditingNews] = useState<News | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    contentImages: [] as string[],
    category: 'alert' as 'alert' | 'tip',
    thumbnailUrl: '',
    isUrgent: false,
    isPublished: true,
    author: '관리자',
    sendNotification: false, // 알림 발송 여부
  });

  useEffect(() => {
    fetchNews();
  }, [page]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/news', {
        params: { page, limit },
      });

      if (response.data.status === 200) {
        setNews(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
      }
    } catch (error) {
      console.error('Failed to fetch news:', error);
      showSnackbar('소식 목록을 불러오는데 실패했습니다', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (newsItem?: News) => {
    if (newsItem) {
      setEditingNews(newsItem);
      setFormData({
        title: newsItem.title,
        description: newsItem.description,
        content: newsItem.content,
        contentImages: newsItem.contentImages || [],
        category: newsItem.category === 'news' ? 'alert' : newsItem.category as 'alert' | 'tip',
        thumbnailUrl: newsItem.thumbnailUrl || '',
        isUrgent: newsItem.isUrgent,
        isPublished: newsItem.isPublished,
        author: newsItem.author,
        sendNotification: false, // 수정 시에는 기본적으로 알림 발송 안함
      });
    } else {
      setEditingNews(null);
      setFormData({
        title: '',
        description: '',
        content: '',
        contentImages: [],
        category: 'alert',
        thumbnailUrl: '',
        isUrgent: false,
        isPublished: true,
        author: '관리자',
        sendNotification: true, // 새 소식 등록 시 기본적으로 알림 발송
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingNews(null);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 파일 크기 체크 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('이미지 크기는 5MB 이하여야 합니다', 'error');
      return;
    }

    // 파일 타입 체크
    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      showSnackbar('JPEG, PNG, GIF, WEBP 형식만 지원합니다', 'error');
      return;
    }

    try {
      setUploading(true);
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const response = await apiClient.post('/upload/news-image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 200) {
        setFormData({ ...formData, thumbnailUrl: response.data.data.url });
        showSnackbar('이미지가 업로드되었습니다', 'success');
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      showSnackbar('이미지 업로드에 실패했습니다', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, thumbnailUrl: '' });
  };

  const handleContentImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    try {
      setUploading(true);
      const uploadedUrls: string[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // 파일 크기 체크 (5MB)
        if (file.size > 5 * 1024 * 1024) {
          showSnackbar(`${file.name}: 이미지 크기는 5MB 이하여야 합니다`, 'error');
          continue;
        }

        // 파일 타입 체크
        if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
          showSnackbar(`${file.name}: JPEG, PNG, GIF, WEBP 형식만 지원합니다`, 'error');
          continue;
        }

        const formDataUpload = new FormData();
        formDataUpload.append('image', file);

        const response = await apiClient.post('/upload/news-image', formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.data.status === 200) {
          uploadedUrls.push(response.data.data.url);
        }
      }

      if (uploadedUrls.length > 0) {
        setFormData({ ...formData, contentImages: [...formData.contentImages, ...uploadedUrls] });
        showSnackbar(`${uploadedUrls.length}개 이미지가 업로드되었습니다`, 'success');
      }
    } catch (error) {
      console.error('Content images upload failed:', error);
      showSnackbar('이미지 업로드에 실패했습니다', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveContentImage = (index: number) => {
    const newImages = formData.contentImages.filter((_, i) => i !== index);
    setFormData({ ...formData, contentImages: newImages });
  };

  const handleSubmit = async () => {
    try {
      if (editingNews) {
        await apiClient.put(`/admin/news/${editingNews._id}`, formData);
        showSnackbar('소식이 수정되었습니다', 'success');
      } else {
        await apiClient.post('/admin/news', formData);
        showSnackbar('소식이 생성되었습니다', 'success');
      }

      handleCloseDialog();
      fetchNews();
    } catch (error) {
      console.error('Failed to save news:', error);
      showSnackbar('소식 저장에 실패했습니다', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await apiClient.delete(`/admin/news/${id}`);
      showSnackbar('소식이 삭제되었습니다', 'success');
      fetchNews();
    } catch (error) {
      console.error('Failed to delete news:', error);
      showSnackbar('소식 삭제에 실패했습니다', 'error');
    }
  };

  const handleTogglePublish = async (id: string, currentStatus: boolean) => {
    try {
      await apiClient.patch(`/admin/news/${id}/publish`, { isPublished: !currentStatus });
      showSnackbar(
        !currentStatus ? '소식이 발행되었습니다' : '소식 발행이 취소되었습니다',
        'success'
      );
      fetchNews();
    } catch (error) {
      console.error('Failed to toggle publish:', error);
      showSnackbar('발행 상태 변경에 실패했습니다', 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'alert': return '피싱 주의보';
      case 'tip': return '보안 팁';
      default: return category;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'alert': return 'error';
      case 'tip': return 'warning';
      default: return 'default';
    }
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">소식 관리</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          소식 추가
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>썸네일</TableCell>
              <TableCell>제목</TableCell>
              <TableCell>카테고리</TableCell>
              <TableCell>긴급</TableCell>
              <TableCell>발행 상태</TableCell>
              <TableCell>조회수</TableCell>
              <TableCell>작성일</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {news.map((item) => (
              <TableRow key={item._id}>
                <TableCell>
                  {item.thumbnailUrl ? (
                    <img
                      src={item.thumbnailUrl}
                      alt={item.title}
                      style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4 }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 80,
                        height: 60,
                        bgcolor: 'grey.200',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        No Image
                      </Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {item.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300, display: 'block' }}>
                    {item.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getCategoryLabel(item.category)}
                    color={getCategoryColor(item.category) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {item.isUrgent && <Chip label="긴급" color="error" size="small" />}
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.isPublished ? '발행됨' : '미발행'}
                    color={item.isPublished ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{item.viewCount}</TableCell>
                <TableCell>
                  {new Date(item.createdAt).toLocaleDateString('ko-KR')}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleTogglePublish(item._id, item.isPublished)}
                    title={item.isPublished ? '발행 취소' : '발행'}
                  >
                    {item.isPublished ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(item)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(item._id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, value) => setPage(value)}
          color="primary"
        />
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingNews ? '소식 수정' : '소식 추가'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="제목"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="설명 (목록에 표시될 짧은 요약)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              required
              helperText="목록에서 보여질 짧은 설명을 입력하세요"
            />
            <TextField
              fullWidth
              label="내용 (상세 페이지에 표시될 전체 내용)"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              multiline
              rows={6}
              required
              helperText="상세 페이지에서 보여질 전체 내용을 입력하세요"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>카테고리</InputLabel>
                <Select
                  value={formData.category}
                  label="카테고리"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                >
                  <MenuItem value="alert">피싱 주의보</MenuItem>
                  <MenuItem value="tip">보안 팁</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="작성자"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
              />
            </Box>

            {/* 썸네일 이미지 업로드 */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>썸네일 이미지</Typography>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleImageUpload}
                ref={fileInputRef}
                style={{ display: 'none' }}
                id="thumbnail-upload"
              />
              {formData.thumbnailUrl ? (
                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                  <img
                    src={formData.thumbnailUrl}
                    alt="Thumbnail preview"
                    style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8 }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleRemoveImage}
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: 'error.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'error.dark' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <label htmlFor="thumbnail-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    disabled={uploading}
                  >
                    {uploading ? '업로드 중...' : '이미지 선택'}
                  </Button>
                </label>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                JPEG, PNG, GIF, WEBP 형식 (최대 5MB)
              </Typography>
            </Box>

            {/* 본문 이미지 업로드 */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>본문 이미지 (여러 장 가능)</Typography>
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleContentImageUpload}
                style={{ display: 'none' }}
                id="content-image-upload"
                multiple
              />
              <label htmlFor="content-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  disabled={uploading}
                >
                  {uploading ? '업로드 중...' : '이미지 추가'}
                </Button>
              </label>
              {formData.contentImages.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {formData.contentImages.map((url, index) => (
                    <Box key={index} sx={{ position: 'relative', display: 'inline-block' }}>
                      <img
                        src={url}
                        alt={`Content ${index + 1}`}
                        style={{ width: 150, height: 150, objectFit: 'cover', borderRadius: 8 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveContentImage(index)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          bgcolor: 'error.main',
                          color: 'white',
                          '&:hover': { bgcolor: 'error.dark' },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                본문에 표시될 이미지들을 추가하세요 (최대 5MB/장)
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isUrgent}
                    onChange={(e) => setFormData({ ...formData, isUrgent: e.target.checked })}
                  />
                }
                label="긴급 소식"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isPublished}
                    onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                  />
                }
                label="즉시 발행"
              />
              {!editingNews && formData.isPublished && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sendNotification}
                      onChange={(e) => setFormData({ ...formData, sendNotification: e.target.checked })}
                      color="success"
                    />
                  }
                  label="모든 사용자에게 푸시 알림 발송"
                />
              )}
            </Box>
            {!editingNews && formData.isPublished && formData.sendNotification && (
              <Alert severity="info" sx={{ mt: 1 }}>
                푸시 알림을 허용한 모든 사용자에게 푸시 알림이 발송됩니다.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingNews ? '수정' : '생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NewsPage;
